<template>
  <div>
    <b-modal
      modal-class="client-form-modal"
      body-class="bg-extra-light-gray p-0"
      footer-class="bg-extra-light-gray"
      v-model="editorShown"
      :title="''"
      @ok="onSave()"
      @close="onCancel()"
      size="md-lg"
      centered
      no-close-on-backdrop
      header-bg-variant="primary"
      header-text-variant="white"
      header-class="bg-purple"
    >
      <template v-slot:modal-header>
        <div
          class="w-100 d-flex flex-row justify-content-between align-items-center"
        >
          <h5 class="modal-title">Invoice for {{ patientName }}</h5>
          <div class="d-flex align-items-center">
            <button
              type="button"
              aria-label="Close"
              class="close text-white"
              @click="onCancel()"
            >
              ×
            </button>
          </div>
        </div>
      </template>

      <template v-slot:modal-footer>
        <div
          class="b-row d-flex justify-content-between align-items-center w-100"
        >
          <div class="b-col">
            <span class="text-danger text-sm-left">{{ error }}</span>
          </div>
          <div class="b-col text-right mt-1 mr-2">
            <b-button @click="onCancel()" variant="secondary">Cancel</b-button>
            <b-button class="ml-2" variant="primary" @click="onSave()"
              >Save
            </b-button>
          </div>
        </div>
      </template>

      <div class="card m-3 px-4 py-4 rounded-lg b-grid justify-content-between">
        <b-row cols="2" align-v="center">
          <b-col>
            <p class="b-title h-auto h3">Invoice</p>
          </b-col>
          <b-col
            class="d-flex justify-content-end flex-column align-items-end text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pb-2">
              {{ systSettingsData?.organizationName ?? "" }}
            </div>
            <div style="line-height:15px; text-align: right">
              <div>
                {{ systSettingsData?.organizationAddressLine1 ?? "" }}
                {{ systSettingsData?.organizationAddressLine2 ?? "" }}
              </div>
              <div>{{ systSettingsData?.organizationCity ?? "" }}</div>
              <div>{{ organizationState }}</div>
              <div>{{ systSettingsData?.organizationZipCode ?? "" }}</div>
            </div>
          </b-col>
        </b-row>
        <hr />
        <b-row class="d-flex justify-content-between align-content-center">
          <b-col
            class="d-flex justify-content-start flex-column align-items-start text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">Bill To</div>
            <div v-for="item in invoice.billTo.split('\n')" :key="item.index">
              {{ item }}
            </div>
          </b-col>
          <b-col
            class="d-flex justify-content-end flex-column align-items-end text-black-50 lower-font-size"
          >
            <div v-if="invoiceData?.refNumber" class="font-weight-bold pt-2">
              Invoice #
            </div>
            <div>{{ invoice.refNumber }}</div>
            <div class="font-weight-bold pt-2">Issued</div>
            <div>
              <date-picker
                size="sm"
                inputWidth="120"
                v-model="invoice.issuedDate"
              />
            </div>
            <div v-if="showDueDate" class="font-weight-bold pt-2">Due</div>
            <div v-if="showDueDate">
              <date-picker
                size="sm"
                inputWidth="120"
                v-model="invoice.dueDate"
              />
            </div>
          </b-col>
        </b-row>
        <b-row class="d-flex justify-content-between align-content-center">
          <b-col
            class="d-flex w-100 justify-content-center flex-column align-items-start text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">Client</div>
            <div class="w-100">
              <b-form-textarea
                v-model="invoice.clientData"
                class="w-100 rounded-lg lower-font-size"
                rows="4"
                no-resize
              />
            </div>
          </b-col>
          <b-col
            class="d-flex w-100 justify-content-center flex-column align-items-start text-black-50 lower-font-size"
          >
            <div class="font-weight-bold pt-2">Provider</div>
            <div class="w-100">
              <b-form-textarea
                v-model="invoice.providerData"
                class="w-100 rounded-lg lower-font-size"
                rows="4"
                no-resize
              />
            </div>
          </b-col>
        </b-row>
        <hr class="pb-2 mb-0" />
        <div class=" w-100">
          <table class="w-100 text-black-50 lower-font-size">
            <thead>
              <tr>
                <th class="font-weight-bold w-25">Date</th>
                <th class="font-weight-bold w-25">Payment Type</th>
                <th class="font-weight-bold w-25">Description</th>
                <th class="font-weight-bold w-25 text-right">Amount</th>
                <th class="font-weight-bold w-25 text-right"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, itemIndex) in lineItemsData"
                :key="item.encounterId"
              >
                <td class="py-2" v-if="!item.manuallyAdded">
                  {{ formatDateOnly(item.date) }}
                </td>
                <td class="py-2" v-if="!item.manuallyAdded">
                  {{
                    item.type == 0
                      ? "Co-Pay"
                      : item.type == 1
                      ? "Deductible/Co-Insurance"
                      : item.type == 2
                      ? "Manual"
                      : item.type == 3
                      ? "Patient Responsibility"
                      : ""
                  }}
                </td>
                <td class="py-2" v-if="!item.manuallyAdded">
                  {{ item.description }}
                </td>
                <td class="text-right py-2" v-if="!item.manuallyAdded">
                  {{ toCurrencyValue(item.amount) }}
                </td>

                <td class="py-2" v-if="item.manuallyAdded">
                  <date-picker
                    size="sm"
                    :value="item.date"
                    @input="
                      newValue => setLineItemValue(itemIndex, 'date', newValue)
                    "
                    :errors="lineItemDateErrors[itemIndex] ? [''] : []"
                  />
                </td>
                <td class="py-2" v-if="item.manuallyAdded">
                  <b-input
                    value="Manual"
                    readonly
                    @input="setLineItemValue(itemIndex, 'type', 2)"
                  >
                  </b-input>
                </td>
                <td class="py-2" v-if="item.manuallyAdded">
                  <b-input
                    size="sm"
                    :value="item.description"
                    @input="
                      newValue =>
                        setLineItemValue(itemIndex, 'description', newValue)
                    "
                    :state="lineItemDescriptionErrors[itemIndex] ? false : null"
                  />
                </td>
                <td
                  class="text-right py-2 d-flex justify-content-end"
                  v-if="item.manuallyAdded"
                >
                  <b-input
                    style="width:100px"
                    size="sm"
                    :value="item.amount"
                    type="number"
                    min="0"
                    @input="
                      newValue =>
                        setLineItemValue(itemIndex, 'amount', newValue)
                    "
                    :state="lineItemAmountErrors[itemIndex] ? false : null"
                  />
                </td>
                <td
                  class="py-2 pl-1"
                  v-if="!item.manuallyAdded ? item.amount > 0 : true"
                >
                  <i
                    class="fa fa-trash text-danger hover-pointer"
                    aria-hidden="true"
                    @click="removeLineItem(itemIndex)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <b-button
            size="sm"
            @click="addLineItem"
            variant="primary"
            class="my-1"
            ><i class="fa fa-plus" aria-hidden="true"></i>
          </b-button>
        </div>
        <hr class="my-0 py-0" />
        <b-row
          cols="2"
          class="py-2 text-black-50 lower-font-size text-right"
          align-h="end"
          align-v="center"
        >
          <b-col cols="2" align-h="end">
            Subtotal
          </b-col>
          <b-col align-h="end" cols="2">
            {{ toCurrencyValue(totalAmount) }}
          </b-col>
        </b-row>
        <hr class="align-self-end my-0 py-0" style="width:35%" />
        <b-row
          cols="2"
          class="py-2 text-black-50 lower-font-size text-right"
          align-h="end"
          align-v="center"
        >
          <b-col cols="2" align-h="end" class="">
            Total
          </b-col>
          <b-col align-h="end" cols="2">
            {{ toCurrencyValue(totalAmount) }}
          </b-col>
        </b-row>
        <b-row
          cols="2"
          class="py-2 text-black-50 lower-font-size text-right"
          align-h="end"
          align-v="center"
        >
          <b-col cols="2" align-h="end">
            Amount Paid
          </b-col>
          <b-col align-h="end" cols="2">
            {{ toCurrencyValue(invoice.amountPaid) }}
          </b-col>
        </b-row>
        <hr class=" align-self-end my-0 py-0" style="width:35%" />
        <b-row
          cols="2"
          class="py-2 lower-font-size text-black-50 font-weight-bold text-right"
          align-h="end"
          align-v="center"
        >
          <b-col cols="2">
            Balance
          </b-col>
          <b-col align-h="end" cols="2" style="font-size:20px;">
            {{
              toCurrencyValue(
                totalAmount >= invoice.amountPaid
                  ? totalAmount - invoice.amountPaid
                  : 0
              )
            }}
          </b-col>
        </b-row>
        <b-row cols="1">
          <b-col cols="12">
            <div class="font-weight-bold lower-font-size text-black-50">
              Notes
            </div>
            <div class="w-100">
              <b-form-textarea
                v-model="invoice.notes"
                class="w-100 rounded-lg lower-font-size"
                rows="4"
                no-resize
              />
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import DatePicker from "../../controls/DatePicker.vue";
import invoiceServer from "../../../server/invoice";
import systemSettingServer from "../../../server/systemSetting";
import eocServer from "../../../server/eoc";
import { mapActions, mapState } from "vuex";
import BillingServer from "../../../server/billing";
import providerServer from "../../../server/user";
import {
  validateDate,
  validateRequired,
  validateNumberPositive
} from "../../../helpers/validation";
import { formatDateOnlyMysql } from "../../../helpers/date";

export default {
  name: "BillingDocumentInvoiceAddEditModal",
  components: { DatePicker },
  props: ["showing", "encounterIds", "episodeOfCareId", "id"],
  data() {
    return {
      error: "",
      invoice: {
        issuedDate: null,
        dueDate: null,
        clientData: "",
        providerData: "",
        notes: "",
        billTo: "",
        totalAmount: 0,
        amountPaid: 0,
        refNumber: null
      },
      invoiceData: null,
      lineItemsData: [],
      systSettingsData: null,
      replaceProviderContactWithLocation: false,
      showDueDate: false,
      patientName: "",
      organizationState: "",
      encounters: [],
      lineItemDateErrors: {},
      lineItemDescriptionErrors: {},
      lineItemAmountErrors: {}
    };
  },
  methods: {
    ...mapActions("client", ["fetchStates"]),
    async fetchInitialData() {
      this.lineItemsData = [];
      this.encounters = [];
      const eoc = await eocServer.get(this.episodeOfCareId);
      for (const x of this.encounterIds) {
        const response = await BillingServer.get(x);
        if (response.uninvoicedAmount > 0) this.encounters.push(response);
      }
      let amount = 0;

      for (const item of this.encounters) {
        let isSelfPay =
          !item.scheduleEventParticipantInsurances.insurancePrimary &&
          !item.scheduleEventParticipantInsurances.insuranceSecundary &&
          !item.scheduleEventParticipantInsurances.insuranceTertiary;
        if (isSelfPay) {
          const lineItemPatientPay = {
            id: null,
            type: 3,
            encounterId: item.id,
            date: item.date,
            description: item.services.map(x => x.name).join(),
            amount: item.uninvoicedAmount,
            manuallyAdded: false
          };
          this.lineItemsData.push(lineItemPatientPay);
        } else {
          let lineItemsEncounter = item.invoicesEncounterLineItem
            .flat()
            .filter(x => x.encounterId == item.id);
          let isLineItemsEncounterCopay = lineItemsEncounter.some(
            x => x.type == 0
          );
          let isLineItemsEncounterDeductible = lineItemsEncounter.some(
            x => x.type == 1
          );
          let isLineItemsEncounterPatientPay = lineItemsEncounter.some(
            x => x.type == 3
          );

          let servicesFullBypatient = item.services.filter(
            x => x.fullyPaidByPatient && x.costPerUnit * x.numberOfUnits > 0
          );
          if (
            servicesFullBypatient.length > 0 &&
            !isLineItemsEncounterPatientPay
          ) {
            const lineItemPatienPay = {
              id: null,
              type: 3,
              encounterId: item.id,
              date: item.date,
              description: servicesFullBypatient.map(x => x.name).join(),
              amount: servicesFullBypatient.reduce(function(acc, element) {
                return acc + element.costPerUnit * element.numberOfUnits;
              }, 0),
              manuallyAdded: false
            };
            this.lineItemsData.push(lineItemPatienPay);
          }
          if (item.copayAmount > 0 && !isLineItemsEncounterCopay) {
            const lineItemCopay = {
              id: null,
              type: 0,
              encounterId: item.id,
              date: item.date,
              description: item.services
                .filter(x => !x.fullyPaidByPatient)
                .map(x => x.name)
                .join(),
              amount: item.copayAmount,
              manuallyAdded: false
            };
            this.lineItemsData.push(lineItemCopay);
          }
          if (
            this.isEnableDeductible &&
            item.deductibleAmount > 0 &&
            !isLineItemsEncounterDeductible
          ) {
            const lineItemDeductible = {
              id: null,
              type: 1,
              encounterId: item.id,
              date: item.date,
              description: item.services
                .filter(x => !x.fullyPaidByPatient)
                .map(x => x.name)
                .join(),
              amount: item.deductibleAmount,
              manuallyAdded: false
            };
            this.lineItemsData.push(lineItemDeductible);
          }
          
          const hasNoLineItemCopayType = !this.lineItemsData.some(x => x.type === 0);
          if (
            item.copayAmount > 0 && 
            item.uninvoicedAmount > 0 && 
            hasNoLineItemCopayType
          ) {
            const copayAmount = item.copayAmount;
            const totalCopayAmountPaid = lineItemsEncounter
              .filter(lineItem => lineItem.type === 0)
              .reduce(
                (acc, currentLineItem) => acc + currentLineItem.amount,
                0
              );
            const copayAmountRemaind = copayAmount - totalCopayAmountPaid;

            if (copayAmountRemaind > 0) {
              this.lineItemsData.push({
                id: null,
                type: 0,
                encounterId: item.id,
                date: item.date,
                description: item.services
                  .filter(x => !x.fullyPaidByPatient)
                  .map(x => x.name)
                  .join(),
                amount: copayAmountRemaind,
                manuallyAdded: false
              });
            }
          }

          const hasNoLineItemDeductibleType = !this.lineItemsData.some(x => x.type === 1)
          if (
            item.deductibleAmount > 0 && 
            item.uninvoicedAmount > 0 &&
            hasNoLineItemDeductibleType
          ) {
            const deductibleAmount = item.deductibleAmount;
            const totalDeductibleAmountPaid = lineItemsEncounter
              .filter(lineItem => lineItem.type === 1)
              .reduce(
                (acc, currentLineItem) => acc + currentLineItem.amount,
                0
              );
            const deductibleAmountRemaind = deductibleAmount - totalDeductibleAmountPaid;

            if(deductibleAmountRemaind > 0) {
              this.lineItemsData.push({
                id: null,
                type: 1,
                encounterId: item.id,
                date: item.date,
                description: item.services
                  .filter(x => !x.fullyPaidByPatient)
                  .map(x => x.name)
                  .join(),
                amount: deductibleAmountRemaind,
                manuallyAdded: false
              });
            }
          }
        }

        amount += item.uninvoicedAmount;
      }
      this.patientName = eoc.patient.name ?? "";
      this.invoice.issuedDate = this.formatDateOnly(new Date());
      var date = new Date();
      this.invoice.dueDate = this.formatDateOnly(
        new Date().setMonth(date.getMonth() + 1)
      );
      this.invoice.clientData =
        this.formatClient(
          eoc.patient.firstName ?? "",
          eoc.patient.middleName ?? "",
          eoc.patient.preferredName ?? "",
          eoc.patient.lastName ?? "",
          ""
        ) +
        "\n" +
        (eoc.patient.phone ?? "") +
        "\n" +
        (eoc.patient.email ?? "");
      if (this.encounters[0]?.providerId != null) {
        const provider = await providerServer.get(
          this.encounters[0].providerId
        );
        this.invoice.providerData =
          provider === ""
            ? ""
            : this.formatUser(provider) +
              "\n" +
              (this.replaceProviderContactWithLocation
                ? eoc.location.phoneNumber
                : provider.phone) +
              "\n" +
              (this.replaceProviderContactWithLocation
                ? eoc.location.email
                : provider.email) +
              "\n";
      } else {
        this.invoice.providerData = "";
      }
      this.invoice.notes = "";
      this.invoice.billTo =
        this.formatClient(
          eoc.patient.firstName ?? "",
          eoc.patient.middleName ?? "",
          eoc.patient.preferredName ?? "",
          eoc.patient.lastName ?? "",
          ""
        ) +
        "\n" +
        (eoc.patient.addressLine1 ?? "") +
        "\n" +
        (eoc.patient.addressLine2 ?? "") +
        "\n" +
        (eoc.patient.city ?? "") +
        "\n" +
        (eoc.patient.state?.abbreviation ?? "") +
        "\n" +
        (eoc.patient.zipcode ?? "");
      this.invoice.amountPaid = 0;
      this.invoice.totalAmount = amount;
    },
    async fetchData() {
      this.invoiceData = await invoiceServer.get(this.id);
      this.lineItemsData = this.invoiceData.lineItems.map(x => ({
        id: x.id,
        encounterId: x.encounterId,
        date: formatDateOnlyMysql(x.date),
        type: x.type,
        description: x.description,
        amount: x.amount,
        manuallyAdded: x.manuallyAdded
      }));
      this.patientName = this.invoiceData.episodeOfCare.patient.name;
      this.invoice.issuedDate =
        this.formatDateOnly(this.invoiceData.dateFrom) ?? null;
      this.invoice.dueDate =
        this.formatDateOnly(this.invoiceData.dateTo) ?? null;
      this.invoice.clientData = this.invoiceData.clientData ?? "";
      this.invoice.providerData = this.invoiceData.providerData ?? "";
      this.invoice.notes = this.invoiceData.notes ?? "";
      this.invoice.billTo = this.invoiceData.billTo;
      this.invoice.amountPaid = this.invoiceData.amountPaid ?? 0;
      this.invoice.totalAmount = this.invoiceData.totalAmount ?? 0;
      this.invoice.refNumber = this.invoiceData.refNumber ?? "";
    },
    addLineItem() {
      this.lineItemsData.push({
        id: null,
        encounterId: null,
        type: 2,
        date: "",
        description: "",
        amount: 0,
        manuallyAdded: true
      });
    },
    removeLineItem(itemIndex) {
      let lineItems = [...this.lineItemsData];
      lineItems.splice(itemIndex, 1);
      this.lineItemsData = lineItems;
    },
    setLineItemValue(itemIndex, field, newValue) {
      let lineItemsData = [...this.lineItemsData];
      lineItemsData[itemIndex][field] = newValue;
      this.lineItemsData = lineItemsData;
    },
    validateSubmit() {
      let withoutErrors = true;
      this.lineItemDateErrors = {};
      this.lineItemDescriptionErrors = {};
      this.lineItemAmountErrors = {};
      for (let index = 0; index < this.lineItemsData.length; index++) {
        const lineItem = this.lineItemsData[index];
        if (lineItem.type != 2) continue;
        if (!validateRequired(lineItem.date)) {
          this.lineItemDateErrors[index] = true;
          withoutErrors = false;
        } else if (lineItem.manuallyAdded && !validateDate(lineItem.date)) {
          this.lineItemDateErrors[index] = true;
          withoutErrors = false;
        }
        if (!validateRequired(lineItem.description)) {
          this.lineItemDescriptionErrors[index] = true;
          withoutErrors = false;
        }
        if (
          !validateRequired(lineItem.amount) ||
          !validateNumberPositive(lineItem.amount)
        ) {
          this.lineItemAmountErrors[index] = true;
          withoutErrors = false;
        }
      }

      return withoutErrors;
    },
    async onSave() {
      const result = await this.validateSubmit();
      if (result) {
        let totalAmount = 0;
        this.lineItemsData
          .map(r => r.amount)
          .forEach(x => {
            totalAmount += parseFloat(x.toString());
          });
        let newInvoice = {
          billTo: this.invoice.billTo,
          clientData: this.invoice.clientData,
          providerData: this.invoice.providerData,
          totalAmount: totalAmount,
          amountPaid: this.invoice.amountPaid,
          notes: this.invoice.notes,
          lineItems: this.lineItemsData.map((l, lIndex) => ({
            ...l,
            order: lIndex
          }))
        };
        if (this.invoice.issuedDate !== null && this.invoice.issuedDate !== "")
          newInvoice.dateFrom = this.formatDateTimeSql(this.invoice.issuedDate);
        if (this.invoice.dueDate !== null && this.invoice.dueDate !== "")
          newInvoice.dateTo = this.formatDateTimeSql(this.invoice.dueDate);
        if (this.id) {
          await invoiceServer
            .update(this.id, newInvoice)
            .then(async () => {
              this.$emit("close");
            })
            .catch(error => {
              this.error = error.message;
            });
        } else {
          newInvoice.episodeOfCareId = this.episodeOfCareId;
          newInvoice.createdById = this.currentUser.id;
          await invoiceServer
            .create(newInvoice)
            .then(async () => {
              this.$emit("close");
            })
            .catch(error => {
              this.error = error.message;
            });
        }
      }
    },
    onCancel() {
      this.$emit("closeModal");
      this.editorShown = false;
    }
  },
  computed: {
    ...mapState("layout", {
      currentUser: state => state.currentUser,
      isEnableDeductible: state => state.systemSetting.enableDeductible
    }),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    ...mapState("client", {
      states: state =>
        state.states.map(s => ({
          label: s.name,
          value: s.id,
          data: s
        }))
    }),
    totalAmount() {
      let total = 0;
      for (let index = 0; index < this.lineItemsData.length; index++) {
        const lineItem = this.lineItemsData[index];
        if (lineItem.amount) {
          total += parseFloat(lineItem.amount.toString());
        }
      }
      return total;
    }
  },
  watch: {
    showing: {
      handler: async function(value) {
        if (value) {
          this.systSettingsData = await systemSettingServer.get();
          this.fetchStates();
          this.lineItemDateErrors = {};
          this.lineItemDescriptionErrors = {};
          this.lineItemAmountErrors = {};
          this.replaceProviderContactWithLocation = this.systSettingsData.invoicesReplaceProviderContactWithLocation;
          if (this.id != null) {
            await this.fetchData();
          } else if (this.showing) {
            await this.fetchInitialData();
          }
          this.organizationState = this.systSettingsData?.organizationState
            ? this.states.find(
                s => s.value === this.systSettingsData?.organizationState
              ).data.abbreviation ?? ""
            : "";
          this.showDueDate = !this.systSettingsData?.invoiceHideDueDate ?? true;
        }
      },
      immediate: true
    }
  }
};
</script>
